import React, { useCallback, useState } from 'react';

import { getGroups } from '@vlabs/api/artemy/client';
import type { GetGroupsParams, GroupId, GroupRecord } from '@vlabs/api/artemy/model';
import { Dropdown, DropdownItem } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import type { AttachPermissionTagGroupProps } from '../types';
import st from './AttachPermissionTagGroupButton.module.sass';
import { useAttachPermissionTagGroup } from './useAttachPermissionTagGroup';

interface GroupOption {
  group: GroupRecord;
  label: string;
  value: GroupId;
}

interface AttachPermissionTagGroupDropdownProps extends AttachPermissionTagGroupProps {
  children: React.ReactElement<{
    disabled?: boolean;
    onClick?: () => void;
  }>;
  disabled?: boolean;
}

interface BuildDropdownOptionsProps {
  availableOptions: GroupOption[];
  emptyLabel: string;
  errorLabel: string;
  handleAttachGroup: (group: GroupRecord) => Promise<void>;
  hasLoadError: boolean;
  isLoadingOptions: boolean;
  isPending: boolean;
}

const GROUPS_PAGE_SIZE = 1000;

async function getAllStreamGroups(page = 1, groups: GroupOption[] = []): Promise<GroupOption[]> {
  const response = await getGroups({
    page,
    page_size: GROUPS_PAGE_SIZE,
  });
  const pageItems = (response.groups ?? []).map((group): GroupOption => ({
    group,
    label: group.group_name,
    value: group.group_id,
  }));
  const nextGroups = [...groups, ...pageItems];

  if (pageItems.length < GROUPS_PAGE_SIZE) {
    return nextGroups;
  }

  return getAllStreamGroups(page + 1, nextGroups);
}

async function getAttachedGroupIds(
  permissionTag: GetGroupsParams['permission_tags'],
  page = 1,
  groupIds: GroupId[] = [],
): Promise<GroupId[]> {
  const response = await getGroups({
    page,
    page_size: GROUPS_PAGE_SIZE,
    permission_tags: permissionTag,
  });
  const responseItems = response.groups ?? [];
  const pageItems = responseItems.map((group) => group.group_id);
  const nextGroupIds = [...groupIds, ...pageItems];

  if (responseItems.length < GROUPS_PAGE_SIZE) {
    return nextGroupIds;
  }

  return getAttachedGroupIds(permissionTag, page + 1, nextGroupIds);
}

function toAvailableOptions(allGroups: GroupOption[], attachedGroupIds: GroupId[]): GroupOption[] {
  const attachedGroupIdsSet = new Set(attachedGroupIds);

  return allGroups
    .filter((group) => !attachedGroupIdsSet.has(group.value))
    .sort((left, right) => left.label.localeCompare(right.label));
}

function buildDropdownOptions({
  availableOptions,
  emptyLabel,
  errorLabel,
  handleAttachGroup,
  hasLoadError,
  isLoadingOptions,
  isPending,
}: BuildDropdownOptionsProps): React.JSX.Element[] {
  if (isLoadingOptions) {
    return [
      <DropdownItem
        disabled
        key="loading"
        label="..."
      />,
    ];
  }

  if (hasLoadError) {
    return [
      <DropdownItem
        disabled
        key="error"
        label={errorLabel}
      />,
    ];
  }

  if (availableOptions.length === 0) {
    return [
      <DropdownItem
        disabled
        key="empty"
        label={emptyLabel}
      />,
    ];
  }

  return availableOptions.map((option) => (
    <DropdownItem
      disabled={isPending}
      key={option.value}
      label={option.label}
      onClick={() => {
        handleAttachGroup(option.group).catch(() => undefined);
      }}
    />
  ));
}

function AttachPermissionTagGroupDropdown({
  children,
  permissionTag,
  disabled = false,
}: AttachPermissionTagGroupDropdownProps): React.JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<GroupOption[]>([]);
  const attachPermissionTagGroup = useAttachPermissionTagGroup({
    permissionTag,
  });
  const isDisabled = disabled || attachPermissionTagGroup.isPending;

  const loadOptions = useCallback(async (): Promise<void> => {
    setIsLoadingOptions(true);
    setHasLoadError(false);

    try {
      const [allGroups, attachedGroupIds] = await Promise.all([
        getAllStreamGroups(),
        getAttachedGroupIds(permissionTag),
      ]);

      setAvailableOptions(toAvailableOptions(allGroups, attachedGroupIds));
    } catch {
      setHasLoadError(true);
      setAvailableOptions([]);
    } finally {
      setIsLoadingOptions(false);
    }
  }, [permissionTag]);

  const handleAttachGroup = useCallback(async (group: GroupRecord): Promise<void> => {
    const result = await attachPermissionTagGroup.attachGroup(group);

    if (result !== 'success') {
      return;
    }

    setAvailableOptions((currentOptions) => (
      currentOptions.filter((currentOption) => currentOption.value !== group.group_id)
    ));
    setIsOpen(false);
  }, [attachPermissionTagGroup]);

  const dropdownOptions = buildDropdownOptions({
    availableOptions,
    emptyLabel: t('features:attach_permission_tag_group.empty') as unknown as string,
    errorLabel: t('features:attach_permission_tag_group.load_error') as unknown as string,
    handleAttachGroup,
    hasLoadError,
    isLoadingOptions,
    isPending: attachPermissionTagGroup.isPending,
  });

  return (
    <Dropdown
      isOpen={isOpen}
      menuClassName={st.Menu}
      onClose={() => setIsOpen(false)}
      options={dropdownOptions}
      placement="bottom-right"
      searchable
    >
      {React.cloneElement(children, {
        disabled: children.props.disabled ?? isDisabled,
        onClick: () => {
          const nextIsOpen = !isOpen;

          children.props.onClick?.();
          setIsOpen(nextIsOpen);

          if (nextIsOpen) {
            loadOptions().catch(() => undefined);
          }
        },
      })}
    </Dropdown>
  );
}

export { AttachPermissionTagGroupDropdown };
